import type { IconI } from "tweenly"
import React from "react"
import { icons } from "lucide-react"
import { renderStyles } from "tweenly"
import { dragNodeRefKey } from "../../../../hooks/editor/useHoldDrag"
import useDynamicRefs from "../../../../hooks/useDynamicRefs"

interface Props {
    object: IconI
    isHiding: boolean
    isLocking: boolean
    isRootTicker: boolean
    canvasScaleCoef: number
    borderCoef: number
    handleSelectObject: (
        event: React.MouseEvent,
        objectId: string,
        animationIds: string[],
        multiSelectKey: boolean
    ) => void
}

/**
 * Convert kebab-case icon name to PascalCase for lucide-react lookup
 * e.g. "cloud-rain" → "CloudRain", "sun" → "Sun", "map-pin" → "MapPin"
 */
const toPascalCase = (name: string): string =>
    name
        .split("-")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join("")

const LUCIDE_FAMILIES = ["lucide", "feather"]

const IconContainer = ({
    object,
    isHiding,
    isLocking,
    isRootTicker,
    handleSelectObject,
}: Props) => {
    // publishes the element so masterTimeline.slice can build GSAP tweens for it — without
    // this the object is simply missing from the ref map and every animation on it is
    // replaced by a no-op ("Target for objectId ... not found")
    const [, setRef] = useDynamicRefs<HTMLDivElement>()
    const registerDragNode = setRef(dragNodeRefKey(object.id))
    const objectStyles: React.CSSProperties = renderStyles(object, "inline")

    const isHidden = isHiding || object.isHidden
    const isLocked = isLocking || object.isLocked

    const handleClick = (e: React.MouseEvent) => {
        if (isLocked || isRootTicker) return
        handleSelectObject(e, object.id, Object.keys(object.animations), e.shiftKey)
    }

    // Resolve icon size from styles (width or height, whichever is numeric)
    const iconSize =
        typeof objectStyles.width === "number"
            ? objectStyles.width
            : typeof objectStyles.height === "number"
            ? objectStyles.height
            : 24

    // Resolve color from styles
    const iconColor = objectStyles.color || "currentColor"

    // Try to render as Lucide SVG component (for lucide/feather families)
    const isLucideFamily = LUCIDE_FAMILIES.includes(object.iconFamily.toLowerCase())
    const pascalName = toPascalCase(object.iconName)
    const LucideIcon = isLucideFamily
        ? (
              icons as Record<
                  string,
                  React.ComponentType<{ size?: number; color?: string; strokeWidth?: number }>
              >
          )[pascalName]
        : undefined

    // Remove font-related styles from wrapper (icon is rendered as SVG, not text)
    const {
        fontFamily: _ff,
        fontSize: _fs,
        lineHeight: _lh,
        color: _c,
        ...wrapperStyles
    } = objectStyles

    const content = (
        <div
            id={object.id}
            ref={setRef(object.id)}
            onClick={handleClick}
            style={{
                ...wrapperStyles,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                visibility: isHidden ? "hidden" : "visible",
                pointerEvents: isLocked || isRootTicker ? "none" : "auto",
            }}
        >
            {LucideIcon ? (
                <LucideIcon
                    size={iconSize}
                    color={typeof iconColor === "string" ? iconColor : undefined}
                />
            ) : (
                // Fallback: CSS ligature rendering for Material Symbols and others
                <span
                    style={{
                        fontFamily: object.iconFamily,
                        fontSize: iconSize,
                        lineHeight: 1,
                        color: typeof iconColor === "string" ? iconColor : undefined,
                        ...(object.iconWeight
                            ? { fontVariationSettings: `'wght' ${object.iconWeight}` }
                            : {}),
                    }}
                >
                    {object.iconName}
                </span>
            )}
        </div>
    )

    /**
     * A flex child is placed by its parent's layout and can never be dragged, so it gets no
     * drag node — the wrapper would become the flex item and this would lose its place in
     * the row. Otherwise `position: relative` makes the wrapper the containing block and
     * gives a multiselection a node to move this object by (see `dragNodeRefKey`); its
     * `transform` belongs to the gesture, never to React.
     */
    if (wrapperStyles.position === "static") return content

    return (
        <div ref={registerDragNode} style={{ position: "relative" }}>
            {content}
        </div>
    )
}

export default IconContainer
